import LaravelError from '@/api/model/LaravelError';
import * as Sentry from '@sentry/vue';
import { useToast } from 'bootstrap-vue-next';
import { ref } from 'vue';

export type Errors = Record<string, string>;

export interface ErrorDetails {
    title: string;
    message: string;
    status?: number;
    isNetworkError: boolean;
    isValidationError: boolean;
}

// Module-level cache so non-component code (e.g. Pinia Colada global hooks) can
// dispatch toasts. Initialised once from a setup context via setupGlobalErrors()
// — see App.vue.
let _global: ReturnType<typeof useErrors> | null = null;

export function setupGlobalErrors(): void {
    _global = useErrors();
}

export function globalErrors(): ReturnType<typeof useErrors> | null {
    return _global;
}

export function useErrors() {
    const { create } = useToast();

    const validationErrors = ref<Errors>({});

    function isNetworkError(error: unknown): boolean {
        if (error instanceof LaravelError && error.localCause) {
            return !error.localCause.response && error.localCause.code === 'ERR_NETWORK';
        }
        return false;
    }

    function isCancelledError(error: unknown): boolean {
        if (error instanceof LaravelError && error.localCause) {
            return error.localCause.code === 'ERR_CANCELED';
        }
        return false;
    }

    function getTitle(error: unknown): string {
        if (isNetworkError(error)) {
            return 'Network Error';
        }

        if (error instanceof LaravelError) {
            switch (error.localCause?.response?.status) {
                case 401:
                    return 'Unauthorized';
                case 403:
                    return 'You do not have permission';
                case 404:
                    return 'Not Found';
                case 422:
                    return 'Validation Error';
                case 429:
                    return 'Too Many Requests';
                case 500:
                    return 'Server Error';
                case 503:
                    return 'Service Unavailable';
                default:
                    return 'Error';
            }
        }

        return 'Error';
    }

    function extractErrorDetails(error: unknown, contextMessage: string): ErrorDetails {
        const isNetwork = isNetworkError(error);
        const title = getTitle(error);
        let message = contextMessage;

        if (isNetwork) {
            message = 'Unable to connect to the server. Please check your internet connection.';
        } else if (error instanceof LaravelError) {
            const status = error.localCause?.response?.status;

            if (status === 401) {
                message = 'Your session has expired. Please log in again.';
            } else if (status === 403) {
                message = contextMessage || 'You do not have permission to perform this action.';
            } else if (status === 429) {
                message = 'Too many requests. Please wait a moment and try again.';
            } else if (error.message) {
                message = `${contextMessage}: ${error.message}`;
            }
        }

        return {
            title,
            message,
            status: error instanceof LaravelError ? error.localCause?.response?.status : undefined,
            isNetworkError: isNetwork,
            isValidationError: error instanceof LaravelError && error.localCause?.response?.status === 422,
        };
    }

    function showErrors(error: unknown, message: string, category: string = 'unknown'): Errors {
        // Silently ignore cancelled requests - these are expected when navigating away or aborting
        if (isCancelledError(error)) {
            return {};
        }

        const details = extractErrorDetails(error, message);

        // Don't send expected errors to Sentry (network, validation, auth/session failures)
        const shouldReport =
            !details.isNetworkError &&
            !details.isValidationError &&
            details.status !== 401 &&
            details.status !== 403 &&
            details.status !== 419;

        if (shouldReport) {
            Sentry.addBreadcrumb({
                category: category ?? 'unknown',
                message: message,
                level: 'error',
            });
            Sentry.captureException(error);
        }

        if (error instanceof Error) {
            console.error(message, error.message, error);
        } else {
            console.error(message, error);
        }

        create({
            title: details.title,
            body: details.message,
            variant: 'danger',
            modelValue: 10000,
            progressProps: {
                variant: 'info',
            },
        });

        if (error instanceof LaravelError) {
            validationErrors.value = Object.fromEntries(
                Object.entries(error.errors ?? {}).map(([key, value]) => [
                    key,
                    Array.isArray(value) ? value.join(', ') : value,
                ]),
            );
        }

        return error instanceof LaravelError ? validationErrors.value : {};
    }

    function clearErrors() {
        validationErrors.value = {};
    }

    function showSuccess(message: string, title: string = 'Success'): void {
        create({
            title,
            body: message,
            variant: 'success',
            modelValue: 5000,
        });
    }

    function showWarning(message: string, title: string = 'Warning'): void {
        create({
            title,
            body: message,
            variant: 'warning',
            modelValue: 7000,
        });
    }

    function showInfo(message: string, title: string = 'Info'): void {
        create({
            title,
            body: message,
            variant: 'info',
            modelValue: 5000,
        });
    }

    return {
        showErrors,
        showSuccess,
        showWarning,
        showInfo,
        validationErrors,
        clearErrors,
        extractErrorDetails,
        isNetworkError,
        isCancelledError,
    };
}
